import { ChevronRight } from 'lucide-react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EmailDialogShell } from '@/Domain/components/Integration/Email/EmailDialogShell';
import { OptionalBadge } from '@/Domain/components/Integration/Email/OptionalBadge';
import { EMAIL_NS } from '@/Domain/components/Integration/Email/email.const';
import type {
  EmailIdentityForm,
  EmailIntegrationParams,
} from '@/Domain/components/Integration/Email/email.types';
import { CopyField } from '@/Domain/components/Integration/components/CopyField';
import { FieldDescription } from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';

interface EmailConfigurationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  params: EmailIntegrationParams | undefined;
  control: Control<EmailIdentityForm>;
  onAddressCopied: () => void;
  onOpenDomain: () => void;
}

export const EmailConfigurationDialog: React.FC<EmailConfigurationDialogProps> = ({
  open,
  onOpenChange,
  params,
  control,
  onAddressCopied,
  onOpenDomain,
}) => {
  const { t } = useTranslation(['integration', 'integrations']);

  return (
    <EmailDialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={t(`${EMAIL_NS}.configure`)}
      testId="email-settings-dialog"
      closeTestId="email-settings-dialog-close"
      doneTestId="email-settings-done-button"
    >
      <div className="space-y-6">
        <div className="space-y-3" data-testid="email-address-section">
          <CopyField
            label={t(`${EMAIL_NS}.address_label`)}
            description={
              <>
                {t(`${EMAIL_NS}.address_help`)}{' '}
                <button
                  type="button"
                  onClick={onOpenDomain}
                  className="group/domain inline-flex cursor-pointer items-baseline gap-0.5 text-foreground link-underline underline-offset-2 transition-colors hover:text-muted-foreground"
                  data-testid="email-custom-domain-link"
                >
                  {t(`${EMAIL_NS}.domain_configure_link`)}
                  <ChevronRight className="size-3.5 shrink-0 translate-y-0.5 transition-transform group-hover/domain:translate-x-0.5" />
                </button>
              </>
            }
            value={params?.inbound_address ?? ''}
            testId="email-address"
            copyTestId="email-address-copy"
            onCopy={onAddressCopied}
          />
        </div>

        <div className="space-y-2" data-testid="email-sender-name-section">
          <div className="flex items-center gap-2">
            <Label htmlFor="email-display-name" className="text-sm font-semibold text-foreground">
              {t(`${EMAIL_NS}.display_name_label`)}
            </Label>
            <OptionalBadge />
          </div>
          <FieldDescription className="pb-0" data-testid="email-display-name-description">
            {t(`${EMAIL_NS}.display_name_help`)}
          </FieldDescription>
          <Controller
            control={control}
            name="display_name"
            render={({ field }) => (
              <Input
                id="email-display-name"
                size="sm"
                value={field.value}
                placeholder={t(`${EMAIL_NS}.display_name_placeholder`)}
                onChange={field.onChange}
                onBlur={field.onBlur}
                data-testid="email-display-name-input"
              />
            )}
          />
        </div>

        <div className="space-y-2" data-testid="email-settings-section">
          <div className="flex items-center gap-2">
            <Label htmlFor="email-signature" className="text-sm font-semibold text-foreground">
              {t(`${EMAIL_NS}.signature_label`)}
            </Label>
            <OptionalBadge />
          </div>
          <FieldDescription className="pb-0" data-testid="email-signature-description">
            {t(`${EMAIL_NS}.signature_help`)}
          </FieldDescription>
          <Controller
            control={control}
            name="signature"
            render={({ field }) => (
              <AutosizeTextarea
                id="email-signature"
                className="px-3"
                minHeight={72}
                value={field.value}
                placeholder={t(`${EMAIL_NS}.signature_placeholder`)}
                onChange={field.onChange}
                onBlur={field.onBlur}
                data-testid="email-signature-input"
              />
            )}
          />
        </div>
      </div>
    </EmailDialogShell>
  );
};
